import { EditContent } from "@/pages/editor";
import { MediaMeta } from "@xnav/types/src/common";
import {
	Box,
	Button,
	Center,
	Flex,
	HStack,
	IconButton,
	Slider,
	SliderFilledTrack,
	SliderThumb,
	SliderTrack,
	Text,
	Tooltip,
	VStack,
	Badge,
	Select,
	Modal,
	ModalOverlay,
	ModalContent
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faPlay,
	faPause,
	faArrowLeft,
	faCircleInfo,
	faVolumeHigh,
	faEye,
	faEyeSlash,
	faForwardStep,
	faRepeat,
	faExpand,
	faDrawPolygon,
	faMapPin,
	faWifi,
	faBatteryFull,
	faRotate,
	faTimes,
	faSignal,
	faMobileScreenButton
} from "@fortawesome/free-solid-svg-icons";
import { useMedia } from "@/hooks/contextHooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { drawContents, scaleMarkup } from "@/functions/canvasFunctions";

const AppleIcon = () => (
	<svg width="12" height="12" viewBox="0 0 170 170" fill="currentColor">
		<path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.69-3.04-7.67-7.88-11.96-14.52-6.79-10.55-12.08-22.38-15.86-35.48-3.79-13.1-5.68-25.18-5.68-36.24 0-14.54 3.73-26.44 11.19-35.69 7.46-9.25 16.75-13.98 27.87-14.19 5.34 0 11.06 1.41 17.16 4.24 6.1 2.83 10.02 4.3 11.75 4.41 1.45-.11 5.5-1.63 12.16-4.57 6.66-2.94 12.5-4.3 17.52-4.08 13.06.65 23.36 5.56 30.9 14.73-11.54 6.96-17.19 16.63-16.95 29.02.22 9.79 4.08 17.9 11.59 24.35 7.51 6.45 16.48 10.12 26.92 11.01-2.29 6.74-5.07 13.88-8.34 21.43zM119.22 31.84c0-7.39 2.66-14.4 7.99-21.03 5.33-6.63 11.95-10.81 19.86-12.54 1.1 8.27-1.42 15.65-7.56 22.14-6.14 6.49-13.62 10.37-22.44 11.64.33-.07.49-.14 2.15-.21z"/>
	</svg>
);

const AndroidIcon = () => (
	<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
		<path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.551 0 .9993.4482.9993.9993.0001.5511-.4483.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993 0 .5511-.4482.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.416.416 0 00-.5676.1521l-2.0223 3.503C15.5896 8.411 13.847 8 12 8s-3.5896.411-5.1368 1.0507L4.8409 5.5477a.416.416 0 00-.5676-.1521.416.416 0 00-.1521.5676l1.9973 3.4592C2.6889 11.1867.3432 14.6589 0 18.761h24c-.3432-4.1021-2.6889-7.5743-6.1185-9.4396"/>
	</svg>
);

export type DeviceType = 'iphone15' | 'iphone15max' | 'galaxyS24' | 'pixel8';

export default function MobileAppPreviewModal({
	isOpen,
	onClose,
	content
}: {
	isOpen: boolean;
	onClose: () => void;
	content: EditContent;
}) {
	const [media] = useMedia() as [MediaMeta[] | undefined, any];
	const [video, setVideo] = useState<MediaMeta>();

	const [videoTime, setVideoTime] = useState<number>(0);
	const [videoPaused, setVideoPaused] = useState<boolean>(true);
	const [videoDuration, setVideoDuration] = useState<number>(0);
	const [isLooping, setIsLooping] = useState<boolean>(false);
	const [playbackRate, setPlaybackRate] = useState<number>(1);
	const [deviceModel, setDeviceModel] = useState<DeviceType>('iphone15');
	const [isLandscape, setIsLandscape] = useState<boolean>(false);

	// Visibility toggles per tag (keyed by `${type}-${index}`)
	const [hiddenTagKeys, setHiddenTagKeys] = useState<Set<string>>(new Set());

	// Info sheet target
	const [selectedInfoTag, setSelectedInfoTag] = useState<{
		title: string;
		description?: string;
		color: string;
		type: string;
		start?: number;
		end?: number;
	} | null>(null);

	// Chapter info modal
	const [isChapterInfoOpen, setIsChapterInfoOpen] = useState(false);

	// Active audio playing
	const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
	const audioRef = useRef<HTMLAudioElement | null>(null);

	const videoRef = useRef<HTMLVideoElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [canvasSize, setCanvasSize] = useState<{ width: number; height: number }>({ width: 340, height: 190 });

	const isIOS = deviceModel.startsWith('iphone');

	// Find video metadata
	useEffect(() => {
		if (media && content) {
			const found = media.find(
				(m: any) =>
					(content.videoId && m.id === content.videoId) ||
					(content.videoTitle && m.name === content.videoTitle) ||
					(content.id && m.references?.includes(content.id)) ||
					(content.title && m.name === content.title)
			);
			if (found && found !== video) {
				setVideo(found);
			}
		}
	}, [media, content, video]);

	// True-to-life Smartphone aspect ratios (19.5:9 / 20:9)
	const deviceDims = useMemo(() => {
		let w = 360;
		let h = 760;
		if (deviceModel === 'iphone15') {
			w = 360;
			h = 760; // 19.5:9 ratio
		} else if (deviceModel === 'iphone15max') {
			w = 385;
			h = 820;
		} else if (deviceModel === 'galaxyS24') {
			w = 355;
			h = 760; // 20:9 ratio
		} else if (deviceModel === 'pixel8') {
			w = 360;
			h = 770; // 20:9 ratio
		}
		return isLandscape ? { width: h, height: w } : { width: w, height: h };
	}, [deviceModel, isLandscape]);

	// Format time helper: 00:00
	const formatSeconds = (sec: number) => {
		const mins = Math.floor(sec / 60);
		const secs = Math.floor(sec % 60);
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	};

	// Adjust canvas dimensions to video aspect
	const adjustCanvasSize = () => {
		const vid = videoRef.current;
		if (!vid) return;
		const vWidth = vid.videoWidth || 16;
		const vHeight = vid.videoHeight || 9;
		const screenW = isLandscape ? deviceDims.width - 32 : deviceDims.width - 16;
		const targetH = isLandscape ? deviceDims.height - 110 : Math.min(220, Math.round((screenW * vHeight) / vWidth));
		const targetW = Math.round((targetH * vWidth) / vHeight);

		const finalW = Math.min(screenW, targetW);
		const finalH = Math.round((finalW * vHeight) / vWidth);

		setCanvasSize({ width: finalW, height: finalH });

		if (canvasRef.current) {
			canvasRef.current.width = finalW;
			canvasRef.current.height = finalH;
		}
	};

	const handleVideoLoaded = () => {
		const vid = videoRef.current;
		if (!vid) return;
		setVideoDuration(vid.duration || 0);
		adjustCanvasSize();
	};

	// Create a filtered content object excluding hidden tags
	const filteredContent = useMemo(() => {
		if (!content) return content;
		const visiblePolygons = (content.polygons || []).filter((_, idx) => !hiddenTagKeys.has(`polygon-${idx}`));
		const visibleHotspots = (content.hotSpots || []).filter((_, idx) => !hiddenTagKeys.has(`hotspot-${idx}`));

		// Scale markup coordinates to match mobile canvas dimensions
		const scaleRatio = canvasSize.width / (content.clientScale ? (videoRef.current?.videoWidth || 600) / content.clientScale : 600);
		const scaled = scaleMarkup(scaleRatio > 0 && isFinite(scaleRatio) ? scaleRatio : 1, {
			...content,
			polygons: visiblePolygons,
			hotSpots: visibleHotspots
		});
		return scaled;
	}, [content, hiddenTagKeys, canvasSize.width]);

	// Draw canvas loop
	const renderCanvas = () => {
		if (!canvasRef.current || !filteredContent) return;
		const ctx = canvasRef.current.getContext("2d");
		if (!ctx) return;
		const t = Math.round(videoTime * 10) / 10;
		drawContents(ctx, filteredContent, t);
	};

	useEffect(() => {
		renderCanvas();
	}, [filteredContent, videoTime]);

	const drawLoop = () => {
		if (videoRef.current && !videoRef.current.paused) {
			setVideoTime(videoRef.current.currentTime);
			renderCanvas();
			requestAnimationFrame(drawLoop);
		}
	};

	useEffect(() => {
		if (!videoRef.current) return;
		if (videoPaused) {
			videoRef.current.pause();
		} else {
			videoRef.current.play();
			requestAnimationFrame(drawLoop);
		}
	}, [videoPaused]);

	useEffect(() => {
		if (!videoRef.current) return;
		videoRef.current.playbackRate = playbackRate;
	}, [playbackRate]);

	// Combine all markups into tag cards list
	const allTags = useMemo(() => {
		if (!content) return [];
		const list: Array<{
			key: string;
			type: 'polygon' | 'hotspot';
			index: number;
			title: string;
			color: string;
			description?: string;
			start: number;
			end: number;
			audios?: Array<{ id: string; name: string }>;
		}> = [];

		(content.polygons || []).forEach((poly, idx) => {
			list.push({
				key: `polygon-${idx}`,
				type: 'polygon',
				index: idx,
				title: poly.title || `Polygon ${idx + 1}`,
				color: poly.color || '#10b981',
				description: poly.description,
				start: poly.start || 0,
				end: poly.end || videoDuration,
				audios: (poly as any).audios
			});
		});

		(content.hotSpots || []).forEach((hs, idx) => {
			list.push({
				key: `hotspot-${idx}`,
				type: 'hotspot',
				index: idx,
				title: hs.title || `Hotspot ${idx + 1}`,
				color: hs.color || '#ec4899',
				description: hs.description,
				start: hs.timeStamps?.[0] || 0,
				end: hs.timeStamps?.[hs.timeStamps.length - 1] || videoDuration,
				audios: (hs as any).audios
			});
		});

		list.sort((a, b) => a.start - b.start);
		return list;
	}, [content, videoDuration]);

	// Toggle tag visibility
	const toggleTagVisibility = (key: string, e: React.MouseEvent) => {
		e.stopPropagation();
		setHiddenTagKeys((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	// Play audio for a tag
	const handlePlayAudio = (tag: typeof allTags[0], e: React.MouseEvent) => {
		e.stopPropagation();
		const audioItem = tag.audios?.[0];
		if (audioItem && media) {
			const foundMedia = media.find((m: any) => m.id === audioItem.id);
			if (foundMedia?.fileUrl) {
				if (audioRef.current) {
					audioRef.current.pause();
				}
				const sound = new Audio(foundMedia.fileUrl);
				audioRef.current = sound;
				setPlayingAudioId(tag.key);
				sound.play();
				sound.onended = () => setPlayingAudioId(null);
				return;
			}
		}

		if ('speechSynthesis' in window) {
			window.speechSynthesis.cancel();
			const utterance = new SpeechSynthesisUtterance(tag.title + (tag.description ? `. ${tag.description}` : ''));
			utterance.rate = 1.0;
			setPlayingAudioId(tag.key);
			utterance.onend = () => setPlayingAudioId(null);
			window.speechSynthesis.speak(utterance);
		}
	};

	const seekToTime = (time: number) => {
		setVideoTime(time);
		if (videoRef.current) {
			videoRef.current.currentTime = time;
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} isCentered size="4xl">
			<ModalOverlay backdropFilter="blur(20px)" bg="rgba(0, 0, 0, 0.82)" />
			<ModalContent
				bg="transparent"
				boxShadow="none"
				border="none"
				display="flex"
				flexDir="column"
				alignItems="center"
				justifyContent="center"
				my="auto"
				p={0}
				maxH="96vh"
			>
				{/* Top Floating Controls Bar */}
				<Flex
					alignItems="center"
					justifyContent="space-between"
					bg="rgba(24, 24, 27, 0.95)"
					backdropFilter="blur(16px)"
					px={3.5}
					py={2}
					borderRadius="2xl"
					border="1px solid rgba(255, 255, 255, 0.12)"
					boxShadow="0 15px 35px rgba(0,0,0,0.6)"
					w="fit-content"
					minW="360px"
					mb={3}
					gap={3}
				>
					<HStack spacing={2}>
						<Badge
							colorScheme={isIOS ? "blue" : "green"}
							variant="solid"
							borderRadius="full"
							px={2.5}
							py={0.5}
							fontSize="2xs"
							fontWeight="700"
							display="flex"
							alignItems="center"
							gap={1.5}
						>
							{isIOS ? <AppleIcon /> : <AndroidIcon />}
							{isIOS ? "iOS Simulator" : "Android Simulator"}
						</Badge>
						<Text fontSize="xs" fontWeight="700" color="gray.200" noOfLines={1} maxW="130px">
							{content.title || "Video Chapter"}
						</Text>
					</HStack>

					<HStack spacing={2}>
						<Select
							size="xs"
							value={deviceModel}
							onChange={(e) => setDeviceModel(e.target.value as any)}
							borderRadius="lg"
							bg="#27272a"
							color="white"
							border="1px solid #3f3f46"
							w="155px"
							fontSize="2xs"
							fontWeight="700"
						>
							<option value="iphone15" style={{ background: '#27272a' }}>iPhone 15 Pro (iOS)</option>
							<option value="iphone15max" style={{ background: '#27272a' }}>iPhone 15 Max (iOS)</option>
							<option value="galaxyS24" style={{ background: '#27272a' }}>Galaxy S24 (Android)</option>
							<option value="pixel8" style={{ background: '#27272a' }}>Pixel 8 (Android)</option>
						</Select>

						<Tooltip label="Rotate Orientation" placement="top" hasArrow>
							<IconButton
								aria-label="Rotate device"
								icon={<FontAwesomeIcon icon={faRotate} />}
								size="xs"
								variant="ghost"
								color="gray.300"
								_hover={{ color: "white", bg: "whiteAlpha.200" }}
								borderRadius="lg"
								onClick={() => setIsLandscape(!isLandscape)}
							/>
						</Tooltip>

						<IconButton
							aria-label="Close Preview"
							icon={<FontAwesomeIcon icon={faTimes} />}
							size="xs"
							variant="ghost"
							color="red.400"
							_hover={{ bg: "red.900", color: "red.200" }}
							borderRadius="lg"
							onClick={onClose}
						/>
					</HStack>
				</Flex>

				{/* Realistic Smartphone Chassis Frame (Narrower & Proportional 19.5:9 / 20:9 Aspect Ratio) */}
				<Box
					w={`${deviceDims.width}px`}
					h={`${deviceDims.height}px`}
					maxH="84vh"
					bg="#09090b"
					borderRadius={isIOS ? "48px" : "36px"}
					p="8px"
					boxShadow="0 30px 90px -15px rgba(0, 0, 0, 0.95), 0 0 0 5px #27272a, 0 0 0 7px #18181b"
					pos="relative"
					display="flex"
					flexDir="column"
					overflow="hidden"
					transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
				>
					{/* iOS Dynamic Island Notch OR Android Camera Hole-Punch */}
					{isIOS ? (
						<Box
							pos="absolute"
							top="10px"
							left="50%"
							transform="translateX(-50%)"
							w="82px"
							h="22px"
							bg="black"
							borderRadius="full"
							zIndex={50}
							display="flex"
							alignItems="center"
							justifyContent="flex-end"
							pr={2}
						>
							<Box w="7px" h="7px" borderRadius="full" bg="#1c1917" border="1px solid #292524" />
						</Box>
					) : (
						<Box
							pos="absolute"
							top="8px"
							left="50%"
							transform="translateX(-50%)"
							w="10px"
							h="10px"
							bg="black"
							borderRadius="full"
							border="1.5px solid #27272a"
							zIndex={50}
						/>
					)}

					{/* Mobile Status Bar (iOS vs Android) */}
					<Flex
						w="100%"
						h="22px"
						px={3.5}
						pt={0.5}
						alignItems="center"
						justifyContent="space-between"
						color="white"
						fontSize="xs"
						fontWeight="600"
						zIndex={40}
						userSelect="none"
					>
						<Text fontSize="10px" fontWeight="700">
							{isIOS ? "9:41" : "10:00"}
						</Text>
						<HStack spacing={1.5}>
							<FontAwesomeIcon icon={faSignal} style={{ fontSize: '8px' }} />
							<FontAwesomeIcon icon={faWifi} style={{ fontSize: '9px' }} />
							<FontAwesomeIcon icon={faBatteryFull} style={{ fontSize: '11px' }} />
						</HStack>
					</Flex>

					{/* Mobile Navigation Header */}
					<Flex
						w="100%"
						alignItems="center"
						justifyContent="space-between"
						px={1.5}
						py={1}
						zIndex={30}
					>
						<HStack spacing={2} minW={0}>
							<IconButton
								aria-label="Back"
								icon={<FontAwesomeIcon icon={faArrowLeft} />}
								size="xs"
								variant="ghost"
								color="white"
								borderRadius="full"
								_hover={{ bg: "whiteAlpha.200" }}
								onClick={onClose}
							/>
							<Text color="white" fontWeight="700" fontSize="xs" noOfLines={1}>
								{content.title || "MCL LAX"}
							</Text>
						</HStack>
						<IconButton
							aria-label="Info"
							icon={<FontAwesomeIcon icon={faCircleInfo} />}
							size="xs"
							variant="ghost"
							color="#10b981"
							borderRadius="full"
							_hover={{ bg: "whiteAlpha.200" }}
							onClick={() => setIsChapterInfoOpen(true)}
						/>
					</Flex>

					{/* Video Player Box */}
					<Box
						w="100%"
						bg="black"
						borderRadius="xl"
						overflow="hidden"
						pos="relative"
						display="flex"
						alignItems="center"
						justifyContent="center"
						minH="150px"
						boxShadow="0 4px 16px rgba(0,0,0,0.6)"
					>
						{video ? (
							<>
								<video
									ref={videoRef}
									src={video.fileUrl}
									style={{
										width: `${canvasSize.width}px`,
										height: `${canvasSize.height}px`,
										objectFit: "contain"
									}}
									onLoadedMetadata={handleVideoLoaded}
									onTimeUpdate={(e) => {
										if (!videoPaused) setVideoTime(e.currentTarget.currentTime);
									}}
									onEnded={() => {
										if (isLooping) {
											seekToTime(0);
											videoRef.current?.play();
										} else {
											setVideoPaused(true);
										}
									}}
								/>
								<canvas
									ref={canvasRef}
									style={{
										position: "absolute",
										top: 0,
										left: 0,
										right: 0,
										bottom: 0,
										width: `${canvasSize.width}px`,
										height: `${canvasSize.height}px`,
										pointerEvents: "none"
									}}
								/>
							</>
						) : (
							<Center p={6} color="gray.500">
								<Text fontSize="2xs">No video media selected</Text>
							</Center>
						)}
					</Box>

					{/* Playback Controls Bar */}
					<Box px={2} pt={2} pb={1}>
						<Flex alignItems="center" justifyContent="space-between" mb={1}>
							<HStack spacing={1.5}>
								<IconButton
									aria-label={videoPaused ? "Play" : "Pause"}
									icon={<FontAwesomeIcon icon={videoPaused ? faPlay : faPause} />}
									size="xs"
									w="28px"
									h="28px"
									minW="28px"
									borderRadius="full"
									bg="#10b981"
									color="white"
									_hover={{ bg: "#059669", transform: "scale(1.06)" }}
									onClick={() => setVideoPaused(!videoPaused)}
									boxShadow="0 2px 8px rgba(16, 185, 129, 0.4)"
								/>
								<Text color="gray.300" fontSize="2xs" fontWeight="700" fontFamily="mono">
									{formatSeconds(videoTime)} <Text as="span" color="gray.600">/ {formatSeconds(videoDuration)}</Text>
								</Text>
							</HStack>

							<HStack spacing={1}>
								<Tooltip label={isLooping ? "Loop On" : "Loop Off"} placement="top">
									<IconButton
										aria-label="Loop"
										icon={<FontAwesomeIcon icon={faRepeat} />}
										size="xs"
										variant="ghost"
										color={isLooping ? "#10b981" : "gray.400"}
										borderRadius="md"
										_hover={{ bg: "whiteAlpha.200" }}
										onClick={() => setIsLooping(!isLooping)}
									/>
								</Tooltip>
								<Tooltip label={`Speed: ${playbackRate}x`} placement="top">
									<Button
										size="xs"
										variant="ghost"
										color="gray.400"
										borderRadius="md"
										fontSize="9px"
										h="20px"
										px={1}
										_hover={{ bg: "whiteAlpha.200", color: "white" }}
										onClick={() => {
											const rates = [0.5, 1, 1.5, 2];
											const next = rates[(rates.indexOf(playbackRate) + 1) % rates.length];
											setPlaybackRate(next);
										}}
									>
										{playbackRate}x
									</Button>
								</Tooltip>
								<Tooltip label="Toggle Orientation" placement="top">
									<IconButton
										aria-label="Fullscreen"
										icon={<FontAwesomeIcon icon={faExpand} />}
										size="xs"
										variant="ghost"
										color="gray.400"
										borderRadius="md"
										_hover={{ bg: "whiteAlpha.200", color: "white" }}
										onClick={() => setIsLandscape(!isLandscape)}
									/>
								</Tooltip>
							</HStack>
						</Flex>

						{/* Scrubber Slider */}
						<Slider
							value={videoTime}
							max={videoDuration || 10}
							step={0.1}
							onChange={(val) => seekToTime(val)}
							focusThumbOnChange={false}
						>
							<SliderTrack bg="gray.800" h="3.5px" borderRadius="full">
								<SliderFilledTrack bg="#10b981" />
							</SliderTrack>
							<SliderThumb boxSize={2.5} bg="white" boxShadow="sm" />
						</Slider>
					</Box>

					{/* Tag Cards List (Under Video) */}
					<Box
						flex={1}
						overflowY="auto"
						px={1.5}
						py={1}
						mt={0.5}
						css={{
							"&::-webkit-scrollbar": { width: "2px" },
							"&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(255,255,255,0.15)", borderRadius: "9999px" },
						}}
					>
						<VStack spacing={1.5} align="stretch" pb={2}>
							{allTags.length === 0 ? (
								<Center py={8} color="gray.600">
									<Text fontSize="2xs">No tags created for this chapter yet</Text>
								</Center>
							) : (
								allTags.map((tag) => {
									const isCurrentActive = videoTime >= tag.start && videoTime <= tag.end;
									const isHidden = hiddenTagKeys.has(tag.key);
									const isAudioPlaying = playingAudioId === tag.key;

									return (
										<Flex
											key={tag.key}
											alignItems="center"
											justifyContent="space-between"
											bg={isCurrentActive ? "#27272a" : "#18181b"}
											borderRadius="lg"
											p={1.5}
											pl={2}
											border="1px solid"
											borderColor={isCurrentActive ? tag.color : "#27272a"}
											boxShadow={isCurrentActive ? `0 0 8px ${tag.color}33` : "none"}
											cursor="pointer"
											transition="all 0.15s"
											_hover={{ bg: "#27272a", transform: "translateY(-1px)" }}
											onClick={() => seekToTime(tag.start)}
										>
											{/* Left Indicator + Icon + Title */}
											<HStack spacing={1.5} minW={0} flex={1}>
												<Box w="3px" h="14px" borderRadius="full" bg={tag.color} flexShrink={0} />
												<Box
													w="18px"
													h="18px"
													borderRadius="md"
													bg={`${tag.color}22`}
													display="flex"
													alignItems="center"
													justifyContent="center"
													flexShrink={0}
												>
													<FontAwesomeIcon
														icon={tag.type === 'polygon' ? faDrawPolygon : faMapPin}
														style={{ fontSize: '9px', color: tag.color }}
													/>
												</Box>
												<Text
													color={isHidden ? "gray.500" : "white"}
													fontSize="2xs"
													fontWeight="700"
													noOfLines={1}
													textDecoration={isHidden ? "line-through" : "none"}
												>
													{tag.title}
												</Text>
											</HStack>

											{/* Right Action Icons: (i), 🔊, 👁️, ⏭ */}
											<HStack spacing={0.5} flexShrink={0}>
												{/* Info Button */}
												<IconButton
													aria-label="Tag details"
													icon={<FontAwesomeIcon icon={faCircleInfo} />}
													size="xs"
													variant="ghost"
													color="gray.400"
													_hover={{ color: "white", bg: "whiteAlpha.200" }}
													borderRadius="full"
													h="20px"
													minW="20px"
													fontSize="10px"
													onClick={(e) => {
														e.stopPropagation();
														setSelectedInfoTag(tag);
													}}
												/>

												{/* Audio Button */}
												<IconButton
													aria-label="Play audio narration"
													icon={<FontAwesomeIcon icon={faVolumeHigh} />}
													size="xs"
													variant="ghost"
													color={isAudioPlaying ? "#10b981" : "gray.400"}
													_hover={{ color: "#10b981", bg: "whiteAlpha.200" }}
													borderRadius="full"
													h="20px"
													minW="20px"
													fontSize="10px"
													onClick={(e) => handlePlayAudio(tag, e)}
												/>

												{/* Visibility Button */}
												<IconButton
													aria-label="Toggle visibility"
													icon={<FontAwesomeIcon icon={isHidden ? faEyeSlash : faEye} />}
													size="xs"
													variant="ghost"
													color={isHidden ? "gray.600" : "gray.400"}
													_hover={{ color: "white", bg: "whiteAlpha.200" }}
													borderRadius="full"
													h="20px"
													minW="20px"
													fontSize="10px"
													onClick={(e) => toggleTagVisibility(tag.key, e)}
												/>

												{/* Jump to start */}
												<IconButton
													aria-label="Jump to tag"
													icon={<FontAwesomeIcon icon={faForwardStep} />}
													size="xs"
													variant="ghost"
													color="gray.400"
													_hover={{ color: "#10b981", bg: "whiteAlpha.200" }}
													borderRadius="full"
													h="20px"
													minW="20px"
													fontSize="10px"
													onClick={(e) => {
														e.stopPropagation();
														seekToTime(tag.start);
													}}
												/>
											</HStack>
										</Flex>
									);
								})
							)}
						</VStack>
					</Box>

					{/* Bottom System Gesture Navigation Bar (iOS Home Pill vs Android 3-Button/Pill) */}
					<Box w="100%" h="16px" display="flex" alignItems="center" justifyContent="center" zIndex={40} userSelect="none">
						{isIOS ? (
							<Box w="100px" h="3.5px" bg="whiteAlpha.400" borderRadius="full" />
						) : (
							<HStack spacing={10} opacity={0.4}>
								<Box w="10px" h="10px" borderLeft="2px solid white" borderBottom="2px solid white" transform="rotate(45deg)" />
								<Box w="10px" h="10px" borderRadius="full" border="2px solid white" />
								<Box w="10px" h="10px" border="2px solid white" borderRadius="1px" />
							</HStack>
						)}
					</Box>
				</Box>
			</ModalContent>

			{/* Sub-modal: Tag Details Sheet */}
			<Modal isOpen={selectedInfoTag !== null} onClose={() => setSelectedInfoTag(null)} isCentered size="xs">
				<ModalOverlay backdropFilter="blur(8px)" bg="rgba(0,0,0,0.6)" />
				<ModalContent bg="#18181b" color="white" borderRadius="2xl" border="1px solid #27272a">
					<Box p={4}>
						<Flex justify="space-between" align="center" mb={2}>
							<HStack spacing={2}>
								<Box w="8px" h="8px" borderRadius="full" bg={selectedInfoTag?.color} />
								<Text fontSize="sm" fontWeight="bold">{selectedInfoTag?.title}</Text>
							</HStack>
							<IconButton
								aria-label="Close"
								icon={<FontAwesomeIcon icon={faTimes} />}
								size="xs"
								variant="ghost"
								onClick={() => setSelectedInfoTag(null)}
							/>
						</Flex>
						<VStack align="stretch" spacing={2.5} mt={2}>
							<HStack justify="space-between" bg="#27272a" p={2} borderRadius="lg">
								<Text fontSize="2xs" color="gray.400">Timestamp</Text>
								<Badge colorScheme="green" fontSize="2xs">
									{selectedInfoTag?.start?.toFixed(1)}s – {selectedInfoTag?.end?.toFixed(1)}s
								</Badge>
							</HStack>
							<Box bg="#27272a" p={2.5} borderRadius="lg">
								<Text fontSize="2xs" fontWeight="700" color="gray.400" textTransform="uppercase" mb={1}>
									Description / Notes
								</Text>
								<Text fontSize="xs" color="gray.200">
									{selectedInfoTag?.description || "No clinical description added for this tag."}
								</Text>
							</Box>
							<Button
								colorScheme="green"
								size="xs"
								borderRadius="lg"
								onClick={() => {
									if (selectedInfoTag?.start !== undefined) {
										seekToTime(selectedInfoTag.start);
									}
									setSelectedInfoTag(null);
								}}
							>
								Jump to Timestamp ({selectedInfoTag?.start?.toFixed(1)}s)
							</Button>
						</VStack>
					</Box>
				</ModalContent>
			</Modal>

			{/* Sub-modal: Chapter Info Sheet */}
			<Modal isOpen={isChapterInfoOpen} onClose={() => setIsChapterInfoOpen(false)} isCentered size="sm">
				<ModalOverlay backdropFilter="blur(8px)" bg="rgba(0,0,0,0.6)" />
				<ModalContent bg="#18181b" color="white" borderRadius="2xl" border="1px solid #27272a">
					<Box p={4}>
						<Flex justify="space-between" align="center" mb={2}>
							<Text fontSize="md" fontWeight="bold">{content.title || "Chapter Info"}</Text>
							<IconButton
								aria-label="Close"
								icon={<FontAwesomeIcon icon={faTimes} />}
								size="xs"
								variant="ghost"
								onClick={() => setIsChapterInfoOpen(false)}
							/>
						</Flex>
						<VStack align="stretch" spacing={2.5} mt={2}>
							<Box bg="#27272a" p={2.5} borderRadius="lg">
								<Text fontSize="2xs" fontWeight="700" color="gray.400" textTransform="uppercase" mb={1}>
									Chapter Overview
								</Text>
								<Text fontSize="xs" color="gray.200">
									{content.description || "Interactive medical video module with tagged anatomical structures and clinical annotations."}
								</Text>
							</Box>
							<HStack justify="space-between" bg="#27272a" p={2} borderRadius="lg">
								<Text fontSize="2xs" color="gray.400">Total Tags / Markups</Text>
								<Badge colorScheme="green" fontSize="2xs">{allTags.length} Active Tags</Badge>
							</HStack>
							<HStack justify="space-between" bg="#27272a" p={2} borderRadius="lg">
								<Text fontSize="2xs" color="gray.400">Duration</Text>
								<Text fontSize="2xs" fontWeight="bold">{formatSeconds(videoDuration)}</Text>
							</HStack>
						</VStack>
					</Box>
				</ModalContent>
			</Modal>
		</Modal>
	);
}
